/**
 * User Store
 * Manages user authentication and profile state
 */

import type { AuthUser, User } from '@/types'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

interface UserState {
	user: User | null
	token: string | null
	isAuthenticated: boolean

	// Actions
	setUser: (authUser: AuthUser) => void
	updateUser: (user: Partial<User>) => void
	logout: () => void

	// Helper methods
	isAdmin: () => boolean
	isStaff: () => boolean
}

export const useUserStore = create<UserState>()(
	devtools(
		persist(
			(set, get) => ({
				user: null,
				token: null,
				isAuthenticated: false,

				setUser: ({ user, token }) =>
					set({
						user,
						token,
						isAuthenticated: true,
					}),

				updateUser: updatedUser =>
					set(state => ({
						user: state.user ? { ...state.user, ...updatedUser } : null,
					})),

				logout: () =>
					set({
						user: null,
						token: null,
						isAuthenticated: false,
					}),

				isAdmin: () => {
					const { user } = get()
					return user?.role === 'admin'
				},

				isStaff: () => {
					const { user } = get()
					return user?.role === 'staff' || user?.role === 'admin'
				},
			}),
			{
				name: 'tattersall-user',
				partialize: state => ({
					user: state.user,
					token: state.token,
					isAuthenticated: state.isAuthenticated,
				}),
			}
		),
		{ name: 'UserStore' }
	)
)
